import { Account, AccountStats } from '@monorepo/types';
import { fetchAccountStatisticsData } from '@hub/utils/react-query/query-functions';
import { useQuery } from '@tanstack/react-query';
import { shouldDisablePolling } from '../../utils/polling-utils';
import {
  ACCOUNT_QUERY_KEYS,
  TRADES_POLLING_INTERVAL,
  TRADES_STALE_TIME,
} from '../constants';

/**
 * Custom hook for fetching account statistics data with real-time polling.
 *
 * Automatically polls for updates every 30 seconds to ensure statistics
 * stay current with backend position sync jobs and trading activity.
 *
 * @param tsAccountId - The trading system account ID to fetch statistics for
 * @returns React Query result object containing account statistics, loading state, and error
 *
 * @example
 * ```tsx
 * const { data: stats, isLoading, error, refetch } = useAccountStatisticsData('TS-123');
 *
 * if (isLoading) return <StatisticsLoader />;
 * if (error) return <ErrorMessage onRetry={refetch} />;
 *
 * return <AccountStatistics data={stats} />;
 * ```
 */
export const useAccountStatisticsData = (
  tsAccountId: string,
  account?: Account,
) => {
  const isPollingDisabled = shouldDisablePolling(account);

  return useQuery<AccountStats>({
    queryKey: ACCOUNT_QUERY_KEYS.ACCOUNT_STATISTICS(tsAccountId),
    queryFn: () => fetchAccountStatisticsData(tsAccountId),
    refetchInterval: isPollingDisabled ? false : TRADES_POLLING_INTERVAL, // Disable polling if account is disabled/paused
    refetchIntervalInBackground: false, // Don't poll when tab is not active
    staleTime: TRADES_STALE_TIME, // Data becomes stale after 25 seconds
    enabled: !!tsAccountId,
  });
};
